import { emptySplitApi as api } from "./baseApi";

// Grading Config types
export interface PerformanceRatingEntry {
  grade: string;
  minPercentage: number;
  maxPercentage: number;
  description?: string;
  colorCode?: string;
}

export interface BonusThresholdEntry {
  minPercentage: number;
  maxPercentage: number;
  bonusEntitlement: number;
}

export interface GradingConfig {
  id?: string;
  configType: string;
  name: string;
  performanceRatings: PerformanceRatingEntry[];
  bonusThresholds: BonusThresholdEntry[];
  isActive: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
}

export const addTagTypes = ["GradingConfigController"] as const;

const gradingConfigApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      // Get active grading configuration
      getActiveGradingConfig: build.query<GradingConfig, void>({
        query: () => ({ url: `/grading-config/active` }),
        providesTags: ["GradingConfigController"],
      }),

      // Get all grading configurations (history)
      getAllGradingConfigs: build.query<GradingConfig[], void>({
        query: () => ({ url: `/grading-config` }),
        providesTags: ["GradingConfigController"],
      }),

      // Create/update grading configuration
      createGradingConfig: build.mutation<GradingConfig, Omit<GradingConfig, 'id' | 'createdAt' | 'updatedAt'>>({
        query: (body) => ({
          url: `/grading-config`,
          method: "POST",
          body,
        }),
        invalidatesTags: ["GradingConfigController"],
      }),

      // Update grading configuration
      updateGradingConfig: build.mutation<void, { id: string; body: Partial<GradingConfig> }>({
        query: ({ id, body }) => ({
          url: `/grading-config/${id}`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: ["GradingConfigController"],
      }),

      // Reset to default configuration
      resetGradingConfigToDefault: build.mutation<GradingConfig, void>({
        query: () => ({
          url: `/grading-config/reset-to-default`,
          method: "POST",
        }),
        invalidatesTags: ["GradingConfigController"],
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetActiveGradingConfigQuery,
  useGetAllGradingConfigsQuery,
  useCreateGradingConfigMutation,
  useUpdateGradingConfigMutation,
  useResetGradingConfigToDefaultMutation,
} = gradingConfigApi;

export default gradingConfigApi;
